// PATH: src/pages/PedagogyPage.tsx — Phase 2 : emploi du temps, notes/bulletins,
// assiduité (avec notification WhatsApp), cahier de texte & devoirs.
//
// Travail QUOTIDIEN uniquement. Les réglages (matières, salles, trimestres,
// coefficients) vivent dans « Admin pédagogique » ; les enseignants dans
// « Personnel ».
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Card, Checkbox, DatePicker, Descriptions, Empty, Form, Input, InputNumber, List, Modal, Popconfirm,
  Segmented, Select, Space, Table, Tabs, Tag, Typography, Upload, message,
} from "antd";
import {
  DownloadOutlined, FilePdfOutlined, InboxOutlined, PlusOutlined, PrinterOutlined, UploadOutlined, WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSchoolApi } from "../api/school";
import { WHATSAPP_ENABLED } from "../config/features";
import { useProfileApi, type SchoolProfile } from "../api/profile";
import {
  adminJustification, usePedagogyApi,
  type Assessment, type AttendanceRecord, type Bulletin, type Score, type TimetableSlot,
  type TimetableImportPreview, type TimetableImportResult, type TimetableImportSlot,
  type TimetableCorrections, type TimetableRef,
} from "../api/pedagogy";
import PageHeader from "../components/PageHeader";
import StudentAvatar from "../components/StudentAvatar";
import ResponsiveTable from "../components/ResponsiveTable";
import { useTabParam } from "../hooks/useTabParam";
import { useSchoolContext } from "../hooks/useSchoolContext";
import { useUnsavedGuard, confirmDiscard } from "../hooks/useUnsavedGuard";
import { notifyWriteError, runUndoable } from "../api/notify";
import { FONT_MONO } from "../theme";
import type { SchoolClass, SchoolYear, Student } from "../models/school";

const DAYS = ["", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const apiError = (err: any) => message.error(err?.response?.data?.error || "Opération refusée");

const PedagogyPage: React.FC = () => {
  const school = useSchoolApi();

  // Référentiels communs aux onglets. L'année et la classe viennent du contexte
  // partagé : elles suivent l'agent d'un écran à l'autre (cf. useSchoolContext).
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const { yearId, classId, setYearId, setClassId } = useSchoolContext();
  const [tab, setTab] = useTabParam("edt");

  useEffect(() => {
    school.listYears().then((ys) => {
      setYears(ys);
      // L'année mémorisée est conservée si elle existe encore ; sinon la plus
      // récente. Sans cette vérification, une année supprimée laisserait tous
      // les écrans sur un identifiant fantôme.
      setYearId((prev) => (prev && ys.some((y) => y.id === prev) ? prev : ys[0]?.id));
    }).catch(() => undefined);
  }, [school, setYearId]);
  useEffect(() => {
    // La classe n'est plus vidée ici : useSchoolContext s'en charge quand
    // l'année CHANGE, sans effacer celle restaurée au montage.
    if (yearId) school.listClasses(yearId).then(setClasses).catch(() => undefined);
    else setClasses([]);
  }, [school, yearId]);
  // Classe mémorisée devenue invalide (supprimée, ou d'une autre année).
  useEffect(() => {
    if (classId && classes.length && !classes.some((c) => c.id === classId)) setClassId(undefined);
  }, [classes, classId, setClassId]);

  const selectedClass = classes.find((c) => c.id === classId);

  return (
    <div>
      <PageHeader
        title="Pédagogie"
        // L'année et la classe conditionnent TOUT le contenu des onglets :
        // les rappeler ici évite de lire un tableau sans savoir de quoi il parle.
        subtitle={
          selectedClass
            ? `${selectedClass.label} · ${years.find((y) => y.id === yearId)?.label ?? ""}`
            : "Sélectionnez une classe pour saisir"
        }
        filters={
          <>
            <Select
              placeholder="Année"
              style={{ width: 150 }}
              value={yearId}
              onChange={setYearId}
              options={years.map((y) => ({ value: y.id, label: y.label }))}
            />
            <Select
              placeholder="Classe"
              style={{ width: 170 }}
              value={classId}
              onChange={setClassId}
              allowClear
              options={classes.map((c) => ({ value: c.id, label: c.label }))}
            />
          </>
        }
      />
      {/* Onglets nus : chaque onglet apporte déjà sa ou ses cartes — les
          envelopper dans une carte de plus donnerait un double encadré. */}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "edt",
            label: "Emploi du temps",
            children: (
              <TimetableTab
                classId={classId}
                yearId={yearId}
                classLabel={selectedClass?.label}
                yearLabel={years.find((y) => y.id === yearId)?.label}
              />
            ),
          },
          { key: "notes", label: "Notes & bulletins", children: <GradebookTab classId={classId} yearId={yearId} /> },
          { key: "assiduite", label: "Assiduité", children: <AttendanceTab classId={classId} yearId={yearId} classLabel={selectedClass?.label} /> },
          { key: "cahier", label: "Cahier de texte & devoirs", children: <TextbookTab classId={classId} /> },
        ]}
      />
    </div>
  );
};

// ── Emploi du temps ──────────────────────────────────────────────────────────
const TimetableTab: React.FC<{
  classId?: number;
  yearId?: number;
  classLabel?: string;
  yearLabel?: string;
}> = ({ classId, yearId, classLabel, yearLabel }) => {
  const api = usePedagogyApi();
  const profileApi = useProfileApi();
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [profile, setProfile] = useState<SchoolProfile | null>(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  // Deux lectures de la même grille : par classe (défaut) ou par enseignant —
  // le backend accepte déjà staff_id, seule l'UI ne l'exploitait pas.
  const [mode, setMode] = useState<"class" | "staff">("class");
  const [staffId, setStaffId] = useState<number>();

  const load = useCallback(() => {
    if (mode === "staff") {
      // Rien tant qu'aucun enseignant n'est choisi : listSlots sans filtre
      // renverrait toute l'école, hors sujet ici.
      if (staffId) api.listSlots(undefined, staffId).then(setSlots).catch(() => undefined);
      else setSlots([]);
    } else {
      api.listSlots(classId).then(setSlots).catch(() => undefined);
    }
  }, [api, mode, classId, staffId]);
  useEffect(load, [load]);
  useEffect(() => {
    api.listSubjects().then(setSubjects);
    api.listStaff().then(setStaff);
    api.listRooms().then(setRooms);
    // En-tête du PDF (nom + logo de l'établissement) : facultatif, comme sur les
    // documents officiels. Un échec (403 hors direction) n'empêche pas l'export.
    profileApi.get().then(setProfile).catch(() => undefined);
  }, [api, profileApi]);

  return (
    <Card extra={
      <Space>
        {/* L'import ne dépend pas de la classe sélectionnée : un classeur peut
            porter une feuille par classe. Seule l'année est indispensable. */}
        <Button icon={<UploadOutlined />} disabled={!yearId} onClick={() => setImportOpen(true)}>
          Importer une grille
        </Button>
        {/* Export PDF via l'impression du navigateur (« Enregistrer en PDF »),
            même mécanisme que les documents officiels — aucune dépendance. */}
        <Button
          icon={<FilePdfOutlined />}
          disabled={slots.length === 0}
          onClick={() => {
            const teacher = staff.find((s) => s.id === staffId);
            printTimetable(slots, {
              classLabel: mode === "class" ? classLabel : undefined,
              staffLabel: mode === "staff" && teacher ? `${teacher.last_name} ${teacher.first_name}` : undefined,
              yearLabel,
              schoolName: profile?.school_name,
              logoUrl: profile?.logo_url,
            });
          }}
        >
          Télécharger le PDF
        </Button>
        <Button icon={<PlusOutlined />} disabled={!classId} onClick={() => setOpen(true)}>Nouveau créneau</Button>
      </Space>
    }>
      {/* Bascule de lecture : par classe (sélecteur de classe dans l'en-tête de
          page) ou par enseignant (sélecteur propre à cet onglet). */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as "class" | "staff")}
          options={[
            { value: "class", label: "Par classe" },
            { value: "staff", label: "Par enseignant" },
          ]}
        />
        {mode === "staff" && (
          <Select
            showSearch
            placeholder="Choisir un enseignant"
            optionFilterProp="label"
            style={{ width: 240 }}
            value={staffId}
            onChange={setStaffId}
            options={staff.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))}
          />
        )}
      </Space>

      {/* Grille, pas liste : un emploi du temps se lit par créneau horaire et
          par jour. En mode enseignant (ou vue toute l'école), une même case peut
          porter plusieurs classes — d'où le libellé de classe dans ce cas. */}
      {mode === "staff" && !staffId ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Choisissez un enseignant pour voir son emploi du temps"
        />
      ) : (
        <TimetableGrid
          slots={slots}
          showClass={mode === "staff" || !classId}
          onDelete={(id) => api.deleteSlot(id).then(load).catch(apiError)}
        />
      )}
      <Modal title="Nouveau créneau" open={open} onCancel={() => setOpen(false)} footer={null} destroyOnClose>
        <Form layout="vertical" initialValues={{ day_of_week: 1 }}
          onFinish={(v) =>
            api.createSlot({ ...v, class_id: classId })
              .then(() => { setOpen(false); load(); })
              .catch((err) => message.error(err?.response?.data?.error || "Conflit ou données invalides", 6))}>
          <Form.Item name="day_of_week" label="Jour" rules={[{ required: true }]}>
            <Select options={DAYS.slice(1).map((d, i) => ({ value: i + 1, label: d }))} />
          </Form.Item>
          <Space>
            <Form.Item name="start_time" label="Début" rules={[{ required: true }]}><Input placeholder="08:00" /></Form.Item>
            <Form.Item name="end_time" label="Fin" rules={[{ required: true }]}><Input placeholder="09:00" /></Form.Item>
          </Space>
          <Form.Item name="subject_id" label="Matière" rules={[{ required: true }]}>
            <Select options={subjects.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="staff_id" label="Enseignant" rules={[{ required: true }]}>
            <Select options={staff.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
          </Form.Item>
          <Form.Item name="room_id" label="Salle">
            <Select allowClear options={rooms.map((r) => ({ value: r.id, label: r.name }))} />
          </Form.Item>
          <Button type="primary" htmlType="submit">Créer</Button>
        </Form>
      </Modal>
      <TimetableImportModal
        open={importOpen}
        yearId={yearId}
        classId={classId}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />
    </Card>
  );
};

// escapeHtml — le PDF est assemblé en HTML brut (chaîne), pas en JSX : les
// libellés saisis par l'école (matières, enseignants, nom d'établissement)
// doivent être échappés pour ne pas casser le balisage ni injecter de contenu.
const escapeHtml = (v: string): string =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

/**
 * printTimetable — export PDF de l'emploi du temps sans dépendance : on
 * construit une vue imprimable dans une fenêtre dédiée et on la confie au moteur
 * d'impression du navigateur (« Enregistrer en PDF »), exactement comme les
 * documents officiels (cf. CertificatePrintPage). La grille est bâtie à partir
 * des données DÉJÀ en mémoire — aucun aller-retour serveur.
 *
 * La fenêtre s'ouvre de façon SYNCHRONE au clic : ouverte après un `await`, un
 * bloqueur de pop-up la refuserait.
 */
const printTimetable = (
  slots: TimetableSlot[],
  opts: { classLabel?: string; staffLabel?: string; yearLabel?: string; schoolName?: string; logoUrl?: string }
): void => {
  // Mêmes axes que TimetableGrid : horaires en lignes, jours en colonnes. Tri
  // alphabétique suffisant, « 08:00 » précédant « 09:00 » sur deux chiffres.
  const times = Array.from(new Set(slots.map((s) => `${s.start_time}–${s.end_time}`))).sort();
  const days = Array.from(new Set(slots.map((s) => s.day_of_week))).sort((a, b) => a - b);
  const cellSlots = (time: string, day: number) =>
    slots.filter((s) => `${s.start_time}–${s.end_time}` === time && s.day_of_week === day);

  // Portée du document : une classe précise si l'utilisateur en a sélectionné
  // une, OU si toutes les séances appartiennent à la même classe (cas courant
  // sans sélection). Sinon l'emploi couvre plusieurs classes, et chaque cellule
  // porte alors son propre libellé.
  const distinctClasses = Array.from(
    new Set(slots.map((s) => s.class_label).filter((l): l is string => !!l))
  );
  // En mode enseignant, la grille couvre plusieurs classes par nature : on
  // n'essaie pas de la ramener à une classe, et chaque case porte la sienne.
  const staffMode = !!opts.staffLabel;
  const resolvedClass = staffMode
    ? undefined
    : opts.classLabel ?? (distinctClasses.length === 1 ? distinctClasses[0] : undefined);
  const showClassCell = staffMode || !resolvedClass;

  const win = window.open("", "_blank", "width=1100,height=800");
  if (!win) {
    message.error("Autorisez les fenêtres pop-up pour télécharger le PDF");
    return;
  }

  const headCells = days.map((d) => `<th>${escapeHtml(DAYS[d])}</th>`).join("");
  const bodyRows = times
    .map((t) => {
      const cells = days
        .map((day) => {
          const cs = cellSlots(t, day);
          if (cs.length === 0) return `<td class="empty">—</td>`;
          const inner = cs
            .map(
              (s) => `<div class="slot">
              <div class="subj">${escapeHtml(s.subject_name ?? "")}</div>
              ${s.staff_name ? `<div class="meta">${escapeHtml(s.staff_name)}</div>` : ""}
              ${s.room_name ? `<div class="room">${escapeHtml(s.room_name)}</div>` : ""}
              ${showClassCell && s.class_label ? `<div class="cls">${escapeHtml(s.class_label)}</div>` : ""}
            </div>`
            )
            .join("");
          return `<td>${inner}</td>`;
        })
        .join("");
      return `<tr><th class="time">${escapeHtml(t)}</th>${cells}</tr>`;
    })
    .join("");

  const scope = staffMode
    ? `Enseignant ${escapeHtml(opts.staffLabel as string)}`
    : resolvedClass
    ? `Classe ${escapeHtml(resolvedClass)}`
    : "Toutes les classes";
  const year = opts.yearLabel ? ` · ${escapeHtml(opts.yearLabel)}` : "";

  win.document.write(`<!doctype html>
<html lang="fr"><head><meta charset="utf-8" />
<title>Emploi du temps — ${scope}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1f1f1f; margin: 0; }
  .head { text-align: center; margin-bottom: 10px; }
  .head img { max-height: 54px; max-width: 170px; object-fit: contain; margin-bottom: 4px; }
  .head h2 { margin: 0; font-size: 15px; }
  .head h1 { margin: 3px 0 1px; font-size: 17px; text-decoration: underline; }
  .head .sub { color: #555; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #999; padding: 3px 6px; vertical-align: top; font-size: 10.5px; }
  thead th { background: #f0f0f0; text-align: center; font-size: 11px; }
  th.time { width: 84px; white-space: nowrap; text-align: center; background: #fafafa; font-family: "Courier New", monospace; }
  td.empty { text-align: center; color: #bbb; }
  .slot { border-left: 3px solid #52c41a; padding-left: 5px; line-height: 1.2; }
  .slot + .slot { margin-top: 5px; }
  .subj { font-weight: 700; }
  .meta { font-size: 10px; }
  .room { font-size: 10px; color: #777; }
  .cls { font-size: 10px; color: #096dd9; margin-top: 1px; }
  .foot { margin-top: 10px; text-align: right; font-size: 10px; color: #777; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="head">
    ${opts.logoUrl ? `<img src="${escapeHtml(opts.logoUrl)}" alt="" />` : ""}
    <h2>${escapeHtml(opts.schoolName || "Établissement scolaire")}</h2>
    <h1>Emploi du temps</h1>
    <div class="sub">${scope}${year}</div>
  </div>
  ${
    times.length === 0
      ? `<p style="text-align:center;color:#888">Aucune séance à l'emploi du temps.</p>`
      : `<table><thead><tr><th class="time">Horaire</th>${headCells}</tr></thead><tbody>${bodyRows}</tbody></table>`
  }
  <div class="foot">Document généré le ${escapeHtml(dayjs().format("DD/MM/YYYY"))}</div>
  <script>
    // Attendre le logo avant d'imprimer, sinon l'en-tête part sans image ; un
    // logo cassé est masqué. Sans logo, on imprime aussitôt.
    (function () {
      var img = document.querySelector('img');
      function go() { window.focus(); window.print(); }
      if (!img) { go(); return; }
      if (img.complete) { go(); return; }
      img.addEventListener('load', go);
      img.addEventListener('error', function () { img.style.display = 'none'; go(); });
      setTimeout(go, 1500);
    })();
  </script>
</body></html>`);
  win.document.close();
};

/**
 * TimetableGrid — l'emploi du temps enregistré, dans sa forme naturelle :
 * horaires en lignes, jours en colonnes. C'est ainsi qu'il est affiché au mur
 * d'une salle des professeurs, et donc ainsi qu'il se relit — une liste triée
 * par jour obligeait à reconstruire mentalement la semaine.
 *
 * Les lignes et colonnes viennent des DONNÉES, pas d'une trame figée : une
 * école qui ne travaille pas le samedi n'a pas de colonne samedi vide, et les
 * créneaux affichés sont les siens.
 */
const TimetableGrid: React.FC<{
  slots: TimetableSlot[];
  showClass: boolean;
  onDelete: (id: number) => void;
}> = ({ slots, showClass, onDelete }) => {
  // Tri alphabétique suffisant : « 08:00 » précède « 09:00 » sur deux chiffres.
  const times = Array.from(new Set(slots.map((s) => `${s.start_time}–${s.end_time}`))).sort();
  const days = Array.from(new Set(slots.map((s) => s.day_of_week))).sort((a, b) => a - b);

  const cellSlots = (time: string, day: number) =>
    slots.filter((s) => `${s.start_time}–${s.end_time}` === time && s.day_of_week === day);

  if (slots.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune séance à l'emploi du temps" />;
  }

  return (
    <Table
      rowKey="time"
      size="small"
      pagination={false}
      scroll={{ x: 140 + days.length * 190 }}
      dataSource={times.map((t) => ({ time: t }))}
      columns={[
        {
          title: "Horaire",
          dataIndex: "time",
          width: 130,
          fixed: "left",
          render: (t: string) => <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{t}</span>,
        },
        ...days.map((day) => ({
          title: DAYS[day],
          key: String(day),
          width: 190,
          render: (_: unknown, row: { time: string }) => {
            const cells = cellSlots(row.time, day);
            if (cells.length === 0) return <span style={{ color: "#d9d9d9" }}>—</span>;
            return cells.map((s) => (
              <div
                key={s.id}
                style={{
                  borderLeft: "3px solid #52c41a",
                  paddingLeft: 8,
                  marginBottom: cells.length > 1 ? 8 : 0,
                  lineHeight: 1.4,
                }}
              >
                <div style={{ fontWeight: 500 }}>{s.subject_name}</div>
                <div style={{ fontSize: 12 }}>{s.staff_name}</div>
                {s.room_name && <div style={{ fontSize: 12, color: "#8c8c8c" }}>{s.room_name}</div>}
                {showClass && s.class_label && (
                  <Tag bordered={false} style={{ marginTop: 2 }}>{s.class_label}</Tag>
                )}
                <Popconfirm title="Supprimer cette séance ?" onConfirm={() => onDelete(s.id)}>
                  <Button type="link" danger size="small" style={{ padding: 0, height: "auto" }}>
                    Supprimer
                  </Button>
                </Popconfirm>
              </div>
            ));
          },
        })),
      ]}
    />
  );
};

/**
 * TimetableImportModal — import d'un emploi du temps au format GRILLE : les
 * jours en colonnes, les horaires en lignes, une feuille par classe.
 *
 * Le contenu des cellules est libre : « Maths / ALAOUI / S3 » comme
 * « ALAOUI / Maths ». C'est le backend qui confronte chaque morceau aux
 * référentiels de l'établissement pour décider ce qui est matière, enseignant
 * ou salle — aucune convention d'écriture n'est imposée à l'école.
 *
 * L'import est TOUT OU RIEN : tant qu'une séance est en erreur, rien n'est
 * écrit. Avec le remplacement activé, un import partiel laisserait des classes
 * sans emploi du temps, ce qui serait pire que de ne rien faire.
 */
const TimetableImportModal: React.FC<{
  open: boolean;
  yearId?: number;
  classId?: number;
  onClose: () => void;
  onImported: () => void;
}> = ({ open, yearId, classId, onClose, onImported }) => {
  const api = usePedagogyApi();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<TimetableImportPreview | null>(null);
  const [result, setResult] = useState<TimetableImportResult | null>(null);
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);
  // Rattachement décidé à l'écran : nom de feuille → identifiant de classe.
  const [sheetClasses, setSheetClasses] = useState<Record<string, number>>({});
  // Associations libellé non reconnu → entité existante, par type.
  const [corrections, setCorrections] = useState<TimetableCorrections>({});

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setReplace(true);
    setSheetClasses({});
    setCorrections({});
  };

  // analyze relance TOUJOURS l'analyse côté serveur : rattachements et
  // associations changent la lecture de la grille, la recalculer ici la ferait
  // diverger de ce qui sera réellement importé.
  const analyze = (
    selected: File,
    assignments: Record<string, number>,
    corr: TimetableCorrections
  ) => {
    if (!yearId) return;
    setBusy(true);
    setFile(selected);
    setResult(null);
    api
      .timetableImportPreview(selected, yearId, classId, assignments, corr)
      .then(setPreview)
      .catch((err) => {
        setPreview(null);
        apiError(err);
      })
      .finally(() => setBusy(false));
  };

  const assignSheet = (sheetName: string, classIdForSheet: number) => {
    const next = { ...sheetClasses, [sheetName]: classIdForSheet };
    setSheetClasses(next);
    if (file) analyze(file, next, corrections);
  };

  // correct associe un libellé du fichier à une entité existante, puis
  // ré-analyse : toutes les cellules portant ce libellé se résolvent d'un coup.
  const correct = (role: keyof TimetableCorrections, label: string, entityId: number) => {
    const next = { ...corrections, [role]: { ...(corrections[role] ?? {}), [label]: entityId } };
    setCorrections(next);
    if (file) analyze(file, sheetClasses, next);
  };

  const run = () => {
    if (!file || !yearId) return;
    setBusy(true);
    api
      .importTimetable(file, yearId, replace, classId, sheetClasses, corrections)
      .then((r) => {
        setResult(r);
        if (r.applied) {
          message.success(`${r.created} séance(s) importée(s)`);
          onImported();
        }
      })
      .catch(apiError)
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Importer un emploi du temps"
      open={open}
      onCancel={onClose}
      afterClose={reset}
      width={1000}
      okText={preview ? `Importer ${preview.valid} séance${preview.valid > 1 ? "s" : ""}` : "Importer"}
      cancelText="Fermer"
      confirmLoading={busy}
      okButtonProps={{ disabled: !preview || preview.valid === 0 || preview.invalid > 0 || !!result?.applied }}
      onOk={run}
    >
      {!preview && (
        <>
          <Upload.Dragger
            accept=".xlsx,.xlsm,.csv"
            maxCount={1}
            showUploadList={false}
            disabled={busy || !yearId}
            beforeUpload={(f) => {
              setSheetClasses({});
              setCorrections({});
              analyze(f as unknown as File, {}, {});
              return false;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Déposez la grille ici, ou cliquez pour la choisir</p>
            <p className="ant-upload-hint">
              Les jours en colonnes, les horaires en lignes. Nommez chaque feuille avec le libellé exact
              de la classe (« 6AP-A ») ; un fichier d'une seule feuille reprend la classe sélectionnée
              à l'écran. L'ordre dans les cellules est libre.
            </p>
          </Upload.Dragger>
          <Button
            style={{ marginTop: 12 }}
            icon={<DownloadOutlined />}
            onClick={() =>
              api
                .timetableTemplate(classId)
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "modele_emploi_du_temps.xlsx";
                  a.click();
                  URL.revokeObjectURL(url);
                })
                .catch(() => message.error("Modèle indisponible"))
            }
          >
            Télécharger le modèle
          </Button>
        </>
      )}

      {preview && (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {preview.invalid > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${preview.invalid} séance(s) en erreur — rien ne sera écrit tant qu'elles subsistent`}
              description="Associez ci-dessous les libellés non reconnus à une entité existante (ou créez-la), sans quitter cette fenêtre."
            />
          )}
          <Space wrap>
            <Tag color="success">{preview.valid} à créer</Tag>
            {preview.invalid > 0 && <Tag color="error">{preview.invalid} en erreur</Tag>}
            {preview.existing_slots > 0 && <Tag>{preview.existing_slots} déjà en place</Tag>}
          </Space>

          {/* Panneau de correspondance : chaque libellé inconnu une seule fois,
              avec un select des entités existantes et un bouton « Créer ». Les
              matières et enseignants sont bloquants ; la salle est facultative. */}
          <CorrespondencePanel
            api={api}
            preview={preview}
            onCorrect={correct}
          />
          <Checkbox checked={replace} onChange={(e) => setReplace(e.target.checked)}>
            Remplacer l'emploi du temps existant des classes présentes dans le fichier
            {preview.existing_slots > 0 && ` (${preview.existing_slots} séance(s) seront supprimées)`}
          </Checkbox>

          {/* Une grille par feuille, dans la disposition du fichier d'origine :
              c'est ainsi que l'utilisateur relit son emploi du temps, et donc
              ainsi qu'il repère ce qui n'a pas été compris. */}
          {preview.sheets.map((sheet) => (
            <SheetGrid
              key={sheet.name}
              sheet={sheet}
              slots={preview.slots}
              classes={preview.classes}
              onAssign={(id) => assignSheet(sheet.name, id)}
            />
          ))}

          {result && !result.applied && (
            <>
              <Alert type="error" showIcon message="Import refusé" description={result.message} />
              {/* Le détail par séance vit dans result.slots (le backend y consigne
                  le conflit exact : ressource en cause, autre classe, horaire). On
                  le montre ici, sinon l'utilisateur ne sait pas QUOI corriger. */}
              {(() => {
                const clashes = result.slots.filter((s) => (s.errors?.length ?? 0) > 0);
                if (clashes.length === 0) return null;
                return (
                  <Card size="small" title={`Séances en conflit (${clashes.length})`}>
                    <List
                      size="small"
                      dataSource={clashes}
                      renderItem={(s) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <span>
                                {s.day_label} {s.start_time}–{s.end_time}
                                {" · "}
                                {s.subject_name || s.raw}
                                {s.staff_name ? ` — ${s.staff_name}` : ""}
                                {s.room_name ? ` (${s.room_name})` : ""}
                              </span>
                            }
                            description={<span style={{ color: "#ff4d4f" }}>{s.errors?.join(" ; ")}</span>}
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                );
              })()}
            </>
          )}
          {result?.applied && (
            <Alert
              type="success"
              showIcon
              message={`${result.created} séance(s) importée(s)`}
              description={result.deleted > 0 ? `${result.deleted} séance(s) remplacée(s).` : undefined}
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

/**
 * CorrespondencePanel — associe les libellés du fichier absents du référentiel
 * à des entités existantes. Chaque libellé n'apparaît QU'UNE fois (dédupliqué
 * par le backend) : l'associer résout d'un coup toutes les cellules qui le
 * portent. Deux gestes possibles par ligne : choisir dans le select des
 * entités existantes, ou « Créer » l'entité à la volée depuis son libellé.
 *
 * Matières et enseignants sont bloquants (une séance sans eux est refusée) ;
 * les salles sont facultatives et regroupées à part.
 */
const CorrespondencePanel: React.FC<{
  api: ReturnType<typeof usePedagogyApi>;
  preview: TimetableImportPreview;
  onCorrect: (role: keyof TimetableCorrections, label: string, entityId: number) => void;
}> = ({ api, preview, onCorrect }) => {
  type Section = {
    role: keyof TimetableCorrections;
    title: string;
    labels: string[];
    options: TimetableRef[];
    create: (label: string) => Promise<number>;
  };
  const sections: Section[] = ([
    {
      role: "subjects",
      title: "Matières",
      labels: preview.unresolved.subjects,
      options: preview.catalog.subjects,
      create: (label) =>
        api.createSubject({ name: label, code: subjectCode(label) }).then((r: any) => r.data.id),
    },
    {
      role: "staff",
      title: "Enseignants",
      labels: preview.unresolved.staff,
      options: preview.catalog.staff,
      create: (label) => {
        const { lastName, firstName } = splitStaffName(label);
        return api.createStaff({ last_name: lastName, first_name: firstName, role: "ENSEIGNANT" })
          .then((r: any) => r.id);
      },
    },
    {
      role: "rooms",
      title: "Salles (facultatif)",
      labels: preview.unresolved.rooms,
      options: preview.catalog.rooms,
      create: (label) => api.createRoom({ name: label }).then((r: any) => r.data.id),
    },
  ] as Section[]).filter((s) => s.labels.length > 0);

  if (sections.length === 0) return null;

  return (
    <Card size="small" title="Correspondances à compléter">
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {sections.map((section) => (
          <div key={section.role}>
            <Typography.Text strong>{section.title}</Typography.Text>
            <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 8 }}>
              {section.labels.map((label) => (
                <CorrespondenceRow
                  key={label}
                  label={label}
                  options={section.options}
                  onSelect={(id) => onCorrect(section.role, label, id)}
                  onCreate={() =>
                    section
                      .create(label)
                      .then((id) => {
                        message.success(`« ${label} » créé`);
                        onCorrect(section.role, label, id);
                      })
                      .catch(apiError)
                  }
                />
              ))}
            </Space>
          </div>
        ))}
      </Space>
    </Card>
  );
};

const CorrespondenceRow: React.FC<{
  label: string;
  options: TimetableRef[];
  onSelect: (id: number) => void;
  onCreate: () => void;
}> = ({ label, options, onSelect, onCreate }) => (
  <Space wrap>
    <Tag color="error" style={{ minWidth: 160, textAlign: "center" }}>{label}</Tag>
    <span>→</span>
    <Select
      showSearch
      placeholder="Choisir l'existant"
      style={{ width: 260 }}
      optionFilterProp="label"
      onChange={onSelect}
      options={options.map((o) => ({ value: o.id, label: o.label }))}
    />
    <Button size="small" icon={<PlusOutlined />} onClick={onCreate}>
      Créer « {label} »
    </Button>
  </Space>
);

// subjectCode dérive un code de matière depuis son nom : accents retirés,
// majuscules, borné. Le backend exige un code non vide à la création.
const subjectCode = (name: string): string => {
  const ascii = name.normalize("NFD").replace(/[̀-ͯ]/g, "");
  const code = ascii.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  return code || "MAT";
};

// splitStaffName sépare « BENNANI Karim » en nom/prénom. Convention marocaine :
// le premier mot est le nom de famille. Un libellé d'un seul mot remplit les
// deux champs (le backend les exige) ; l'école ajuste ensuite dans Personnel.
const splitStaffName = (label: string): { lastName: string; firstName: string } => {
  const parts = label.trim().split(/\s+/);
  if (parts.length < 2) return { lastName: label.trim(), firstName: label.trim() };
  return { lastName: parts[0], firstName: parts.slice(1).join(" ") };
};

/**
 * SheetGrid — restitue une feuille du fichier dans SA disposition : horaires en
 * lignes, jours en colonnes. Chaque cellule porte ce que le serveur a compris,
 * et en rouge ce qu'il n'a pas su lire, avec le texte d'origine à côté — c'est
 * ce qui permet de corriger le tableur sans deviner quelle cellule est en cause.
 */
const SheetGrid: React.FC<{
  sheet: { name: string; class_label?: string; error?: string; slots: number };
  slots: TimetableImportSlot[];
  classes: { id: number; label: string }[];
  onAssign: (classId: number) => void;
}> = ({ sheet, slots, classes, onAssign }) => {
  const mine = slots.filter((s) => s.sheet === sheet.name);

  // Les créneaux horaires du fichier, dans l'ordre de la journée. Le tri
  // alphabétique suffit : « 08:00 » précède « 09:00 » sur deux chiffres.
  const times = Array.from(new Set(mine.map((s) => `${s.start_time}–${s.end_time}`))).sort();
  const days = Array.from(new Set(mine.map((s) => s.day_of_week))).sort((a, b) => a - b);

  const cellOf = (time: string, day: number) =>
    mine.find((s) => `${s.start_time}–${s.end_time}` === time && s.day_of_week === day);

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>Feuille « {sheet.name} »</span>
          {sheet.class_label ? <Tag color="blue">{sheet.class_label}</Tag> : <Tag color="error">classe à choisir</Tag>}
        </Space>
      }
      extra={
        sheet.error ? (
          <Select
            placeholder="Rattacher à une classe"
            style={{ width: 220 }}
            onChange={onAssign}
            options={classes.map((c) => ({ value: c.id, label: c.label }))}
          />
        ) : (
          <Typography.Text type="secondary">{sheet.slots} séance(s)</Typography.Text>
        )
      }
    >
      {sheet.error && <Alert type="warning" showIcon message={sheet.error} style={{ marginBottom: 8 }} />}
      {!sheet.error && times.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune séance lue dans cette feuille" />
      )}
      {times.length > 0 && (
        <Table
          rowKey="time"
          size="small"
          pagination={false}
          scroll={{ x: 200 + days.length * 200 }}
          dataSource={times.map((t) => ({ time: t }))}
          columns={[
            {
              title: "Horaire",
              dataIndex: "time",
              width: 130,
              render: (t: string) => <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{t}</span>,
            },
            ...days.map((day) => ({
              title: DAYS[day],
              key: String(day),
              width: 200,
              render: (_: unknown, row: { time: string }) => {
                const slot = cellOf(row.time, day);
                if (!slot) return <span style={{ color: "#d9d9d9" }}>—</span>;
                const broken = (slot.errors?.length ?? 0) > 0;
                return (
                  <div
                    style={{
                      borderLeft: `3px solid ${broken ? "#ff4d4f" : "#52c41a"}`,
                      paddingLeft: 8,
                      lineHeight: 1.4,
                    }}
                  >
                    {broken ? (
                      <>
                        <div style={{ fontSize: 12 }}>{slot.raw}</div>
                        <div style={{ color: "#ff4d4f", fontSize: 12 }}>{slot.errors?.join(" ; ")}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 500 }}>{slot.subject_name}</div>
                        <div style={{ fontSize: 12 }}>{slot.staff_name}</div>
                        {slot.room_name && (
                          <div style={{ fontSize: 12, color: "#8c8c8c" }}>{slot.room_name}</div>
                        )}
                      </>
                    )}
                    {(slot.warnings?.length ?? 0) > 0 && (
                      <div style={{ color: "#d48806", fontSize: 12 }}>{slot.warnings?.join(" ; ")}</div>
                    )}
                  </div>
                );
              },
            })),
          ]}
        />
      )}
    </Card>
  );
};

/**
 * printBulletins — export PDF d'un ou plusieurs bulletins, même mécanisme que
 * l'emploi du temps et les documents officiels : une vue imprimable dans une
 * fenêtre dédiée, confiée au moteur d'impression du navigateur. Un bulletin par
 * page (page-break), pour un tirage de classe entière en une passe.
 *
 * La fenêtre est ouverte par l'appelant AVANT toute attente réseau (le lot
 * charge les bulletins de façon asynchrone) : sinon un bloqueur de pop-up la
 * refuserait.
 */
const printBulletins = (
  win: Window,
  bulletins: Bulletin[],
  opts: { schoolName?: string; logoUrl?: string }
): void => {
  const fmt = (v?: number) => (v != null ? v.toFixed(2) : "—");
  const pages = bulletins
    .map((b) => {
      // Colonne d'appréciations imprimée seulement si au moins une matière en
      // porte une : sinon le bulletin sort avec une colonne vide sur toute sa
      // hauteur.
      const hasAppreciations = b.subjects.some((s) => s.appreciation);
      const rows = b.subjects
        .map(
          (s) => `<tr>
            <td>${escapeHtml(s.subject_name)}</td>
            <td class="num">${escapeHtml(String(s.coefficient))}</td>
            <td class="num">${fmt(s.average)}</td>
            <td class="num">${s.assessment_count}</td>
            ${hasAppreciations ? `<td class="appr">${escapeHtml(s.appreciation ?? "")}</td>` : ""}
          </tr>`
        )
        .join("");
      return `<section class="bulletin">
        <div class="head">
          ${opts.logoUrl ? `<img src="${escapeHtml(opts.logoUrl)}" alt="" />` : ""}
          <h2>${escapeHtml(opts.schoolName || "Établissement scolaire")}</h2>
          <h1>Bulletin — ${escapeHtml(b.term_label)}</h1>
        </div>
        <div class="info">
          <div><strong>Élève :</strong> ${escapeHtml(b.student_name)}</div>
          <div><strong>Classe :</strong> ${escapeHtml(b.class_label || "—")}${
        b.level_name ? ` (${escapeHtml(b.level_name)})` : ""
      }</div>
        </div>
        <table>
          <thead><tr>
            <th>Matière</th><th class="num">Coef</th><th class="num">Moyenne /20</th><th class="num">Évaluations</th>
            ${hasAppreciations ? `<th>Appréciation</th>` : ""}
          </tr></thead>
          <tbody>${rows || `<tr><td colspan="${hasAppreciations ? 5 : 4}" class="empty">Aucune matière évaluée</td></tr>`}</tbody>
        </table>
        <div class="overall">
          Moyenne générale : <strong>${fmt(b.overall_average)} / 20</strong>
          ${b.rank && b.class_size ? `<span class="rank">Rang : <strong>${b.rank}</strong> / ${b.class_size}</span>` : ""}
        </div>
        ${
          b.general_appreciation
            ? `<div class="general"><strong>Appréciation générale :</strong> ${escapeHtml(b.general_appreciation)}</div>`
            : ""
        }
        <div class="sign">
          <div>Fait le ${escapeHtml(dayjs().format("DD/MM/YYYY"))}</div>
          <div class="cachet">Signature et cachet<div class="box"></div></div>
        </div>
      </section>`;
    })
    .join("");

  win.document.open();
  win.document.write(`<!doctype html>
<html lang="fr"><head><meta charset="utf-8" />
<title>Bulletin${bulletins.length > 1 ? "s" : ""}</title>
<style>
  @page { size: A4 portrait; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1f1f1f; margin: 0; }
  .bulletin:not(:last-child) { page-break-after: always; }
  .head { text-align: center; margin-bottom: 18px; }
  .head img { max-height: 70px; max-width: 200px; object-fit: contain; margin-bottom: 8px; }
  .head h2 { margin: 0; font-size: 18px; }
  .head h1 { margin: 6px 0 0; font-size: 20px; text-decoration: underline; }
  .info { margin: 20px 0; line-height: 1.9; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #999; padding: 7px 10px; font-size: 13px; }
  thead th { background: #f0f0f0; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  td.empty { text-align: center; color: #888; }
  .overall { text-align: right; font-size: 15px; margin-top: 14px; }
  .overall .rank { margin-left: 22px; }
  td.appr { font-size: 12px; font-style: italic; }
  .general { margin-top: 16px; border: 1px solid #999; padding: 10px 12px; font-size: 13px; line-height: 1.6; }
  .sign { display: flex; justify-content: space-between; margin-top: 48px; }
  .sign .box { height: 70px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  ${pages}
  <script>
    (function () {
      var img = document.querySelector('img');
      function go() { window.focus(); window.print(); }
      if (!img) { go(); return; }
      if (img.complete) { go(); return; }
      img.addEventListener('load', go);
      img.addEventListener('error', function () { img.style.display = 'none'; go(); });
      setTimeout(go, 1500);
    })();
  </script>
</body></html>`);
  win.document.close();
};

// ── Notes & bulletins ────────────────────────────────────────────────────────
const GradebookTab: React.FC<{ classId?: number; yearId?: number }> = ({ classId, yearId }) => {
  const api = usePedagogyApi();
  const school = useSchoolApi();
  const [terms, setTerms] = useState<any[]>([]);
  const [termId, setTermId] = useState<number>();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [open, setOpen] = useState(false);
  const [scoring, setScoring] = useState<Assessment | null>(null);
  const [scores, setScores] = useState<Score[]>([]);
  const [bulletin, setBulletin] = useState<Bulletin | null>(null);
  // Brouillon des appréciations : la saisie ne modifie pas le bulletin affiché
  // tant qu'elle n'est pas enregistrée.
  const [apprGeneral, setApprGeneral] = useState("");
  const [apprSubjects, setApprSubjects] = useState<Record<number, string>>({});
  const [apprSaving, setApprSaving] = useState(false);
  // Saisies en cours : gardent contre la fermeture d'onglet et contre la
  // fermeture d'une modale par mégarde.
  const [scoresDirty, setScoresDirty] = useState(false);
  const [apprDirty, setApprDirty] = useState(false);
  useUnsavedGuard(scoresDirty || apprDirty);
  const [assessmentForm] = Form.useForm();
  const profileApi = useProfileApi();
  const [profile, setProfile] = useState<SchoolProfile | null>(null);
  const [printingBatch, setPrintingBatch] = useState(false);
  // Saisie au clavier : chaque champ de note s'enregistre ici pour que Entrée
  // (ou ↑/↓) donne le focus au suivant, sans repasser par la souris.
  const scoreInputs = useRef<({ focus: () => void } | null)[]>([]);
  const focusScore = (i: number) => scoreInputs.current[i]?.focus();

  // teachersOf : enseignants dont les matières (réglées dans Personnel) incluent
  // celle passée — sert à pré-remplir les surveillants d'une évaluation avec le
  // ou les profs de la matière.
  const teachersOf = (subjectId: number): number[] =>
    staff.filter((s) => (s.subject_ids ?? []).includes(subjectId)).map((s) => s.id);

  useEffect(() => {
    if (yearId) api.listTerms(yearId).then((ts) => { setTerms(ts); if (ts.length) setTermId(ts[0].id); });
    api.listSubjects().then(setSubjects);
    api.listStaff().then(setStaff);
    api.listRooms().then(setRooms);
  }, [api, yearId]);
  // En-tête du bulletin (nom + logo de l'établissement), comme les documents
  // officiels. Facultatif : un échec (403 hors direction) n'empêche pas le tirage.
  useEffect(() => { profileApi.get().then(setProfile).catch(() => undefined); }, [profileApi]);

  // Impression d'un bulletin déjà chargé (fenêtre ouverte de façon synchrone au
  // clic, sinon le bloqueur de pop-up la refuserait).
  const printBulletinPdf = (bulletins: Bulletin[]) => {
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) { message.error("Autorisez les fenêtres pop-up pour imprimer le PDF"); return; }
    printBulletins(win, bulletins, { schoolName: profile?.school_name, logoUrl: profile?.logo_url });
  };

  // Tirage de la classe entière : la fenêtre s'ouvre TOUT DE SUITE (un bloqueur
  // refuserait une ouverture différée), puis on y écrit les bulletins une fois
  // tous chargés. Les élèves sans note produisent un bulletin vide, jamais une
  // erreur bloquante.
  const printAllBulletins = () => {
    if (!termId || students.length === 0) return;
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) { message.error("Autorisez les fenêtres pop-up pour imprimer les bulletins"); return; }
    win.document.write(`<p style="font-family:sans-serif;padding:24px;color:#666">Génération des bulletins…</p>`);
    setPrintingBatch(true);
    Promise.all(students.map((st) => api.getBulletin(st.id, termId).catch(() => null)))
      .then((results) => {
        const bulletins = results.filter((b): b is Bulletin => b != null);
        if (bulletins.length === 0) { win.close(); message.warning("Aucun bulletin disponible pour cette classe"); return; }
        printBulletins(win, bulletins, { schoolName: profile?.school_name, logoUrl: profile?.logo_url });
      })
      .catch(() => { win.close(); message.error("Impossible de générer les bulletins"); })
      .finally(() => setPrintingBatch(false));
  };

  const load = useCallback(() => {
    if (classId && termId) api.listAssessments(classId, termId).then(setAssessments);
  }, [api, classId, termId]);
  useEffect(load, [load]);
  useEffect(() => {
    if (classId && yearId) {
      school.listStudents({ class_id: classId, school_year_id: yearId, page_size: 200 })
        .then((r) => setStudents(r.items ?? []));
    }
  }, [school, classId, yearId]);

  // Le brouillon d'appréciations suit le bulletin ouvert, quel que soit le
  // chemin d'ouverture (clic sur la ligne ou sur le bouton).
  useEffect(() => {
    setApprGeneral(bulletin?.general_appreciation ?? "");
    setApprSubjects(
      Object.fromEntries((bulletin?.subjects ?? []).map((s) => [s.subject_id, s.appreciation ?? ""]))
    );
    setApprDirty(false);
  }, [bulletin]);

  const saveAppreciations = () => {
    if (!bulletin || !termId) return;
    setApprSaving(true);
    api.setAppreciations(bulletin.student_id, termId, { general: apprGeneral, subjects: apprSubjects })
      .then((fresh) => { setBulletin(fresh); setApprDirty(false); message.success("Appréciations enregistrées"); })
      .catch((err) => notifyWriteError(err, { title: "Appréciations non enregistrées", retry: saveAppreciations }))
      .finally(() => setApprSaving(false));
  };

  // saveScores : l'échec ne ferme PAS la modale et la notification reste
  // affichée — une classe entière de saisie ne doit pas disparaître sur un
  // toast de trois secondes.
  const saveScores = () =>
    api
      .putScores(scoring!.id, scores.map(({ student_id, score, is_absent, comment }) => ({ student_id, score, is_absent, comment })))
      .then(() => {
        message.success("Notes enregistrées");
        setScoresDirty(false);
        setScoring(null);
      })
      .catch((err) => notifyWriteError(err, { title: "Notes non enregistrées", retry: saveScores }));

  const openScores = (a: Assessment) => {
    // Les champs de la saisie précédente n'existent plus (destroyOnClose) :
    // repartir d'un tableau vide évite de garder des références mortes.
    scoreInputs.current = [];
    setScoresDirty(false);
    api.getScores(a.id).then((existing) => {
      const byStudent = new Map(existing.map((s) => [s.student_id, s]));
      setScores(students.map((st) => byStudent.get(st.id) ?? { student_id: st.id, student_name: `${st.last_name} ${st.first_name}` }));
      setScoring(a);
    });
  };

  const termLabel = terms.find((t) => t.id === termId)?.label;

  return (
    // Deux sujets distincts, deux cartes : on saisit des évaluations, on
    // consulte des bulletins. L'ancien titre « Bulletins » posé au milieu de la
    // carte des évaluations les faisait passer pour une suite du tableau.
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="Évaluations"
        extra={
          <Space>
            <Select placeholder="Trimestre" style={{ width: 150 }} value={termId} onChange={setTermId}
              options={terms.map((t) => ({ value: t.id, label: t.label }))} />
            <Button type="primary" icon={<PlusOutlined />} disabled={!classId || !termId} onClick={() => setOpen(true)}>
              Nouvelle évaluation
            </Button>
          </Space>}>
        <ResponsiveTable<Assessment> rowKey="id" dataSource={assessments} pagination={false} size="small"
          scroll={{ x: 850 }}
          card={{
            title: (a) => a.title,
            extra: (a) => (
              <Tag bordered={false} color={a.kind === "EXAMEN" ? "blue" : "default"}>
                {a.kind === "EXAMEN" ? "Examen" : "Contrôle continu"}
              </Tag>
            ),
            description: (a) => (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {a.subject_name} · barème /{a.max_score} · coef {a.coefficient}
              </Typography.Text>
            ),
            actions: (a) => [
              <Button key="scores" size="small" type="text" onClick={() => openScores(a)}>Saisir les notes</Button>,
              <Popconfirm key="del" title="Supprimer ?" onConfirm={() => api.deleteAssessment(a.id).then(load)}>
                <Button size="small" danger type="text">Supprimer</Button>
              </Popconfirm>,
            ],
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={!classId ? "Sélectionnez une classe" : "Aucune évaluation ce trimestre"}
              />
            ),
          }}
          columns={[
            { title: "Titre", dataIndex: "title", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            { title: "Matière", dataIndex: "subject_name" },
            { title: "Type", dataIndex: "kind", width: 150,
              render: (k: string) => (
                <Tag bordered={false} color={k === "EXAMEN" ? "blue" : "default"}>
                  {k === "EXAMEN" ? "Examen" : "Contrôle continu"}
                </Tag>) },
            { title: "Barème", dataIndex: "max_score", width: 90,
              render: (v) => <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>/{v}</span> },
            { title: "Coef", dataIndex: "coefficient", width: 70,
              render: (v) => <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{v}</span> },
            { title: "", width: 240, align: "right", render: (_, a) => (
              <Space>
                <Button size="small" onClick={() => openScores(a)}>Saisir les notes</Button>
                <Popconfirm title="Supprimer ?" onConfirm={() => api.deleteAssessment(a.id).then(load)}>
                  <Button size="small" danger type="text">Supprimer</Button>
                </Popconfirm>
              </Space>) },
          ]} />
      </Card>

      <Card
        title="Bulletins"
        // Le trimestre se choisit au-dessus : le rappeler ici évite d'ouvrir un
        // bulletin sans savoir de quelle période il parle.
        extra={
          <Space>
            {termLabel && <Typography.Text type="secondary">{termLabel}</Typography.Text>}
            <Button
              icon={<PrinterOutlined />}
              loading={printingBatch}
              disabled={!classId || !termId || students.length === 0}
              onClick={printAllBulletins}
            >
              Imprimer les bulletins
            </Button>
          </Space>
        }
      >
        <ResponsiveTable<Student> rowKey="id" dataSource={students} pagination={false} size="small"
          showHeader={false}
          // Ouvrir la carte revient à ouvrir le bulletin : c'est la seule
          // chose qu'on vienne faire dans cette liste.
          onRowClick={(st) => termId && api.getBulletin(st.id, termId).then(setBulletin).catch(apiError)}
          card={{
            title: (st) => `${st.last_name} ${st.first_name}`,
            extra: () => <Typography.Text type="secondary" style={{ fontSize: 12 }}>Voir le bulletin</Typography.Text>,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={!classId ? "Sélectionnez une classe" : "Aucun élève inscrit dans cette classe"}
              />
            ),
          }}
          onRow={(st) => ({
            onClick: () => termId && api.getBulletin(st.id, termId).then(setBulletin).catch(apiError),
            style: { cursor: termId ? "pointer" : "default" },
          })}
          columns={[
            { title: "Élève", render: (_, st) => (
              <Space size={12}>
                <StudentAvatar id={st.id} lastName={st.last_name} firstName={st.first_name} size={28} />
                <span style={{ fontWeight: 500 }}>{st.last_name} {st.first_name}</span>
              </Space>) },
            { title: "", align: "right", width: 150, render: (_, st) => (
              <Button size="small" type="link" disabled={!termId}
                onClick={() => api.getBulletin(st.id, termId!).then(setBulletin).catch(apiError)}>
                Voir le bulletin
              </Button>) },
          ]} />
      </Card>

      <Modal title="Nouvelle évaluation" open={open} onCancel={() => setOpen(false)} footer={null} destroyOnClose>
        <Form form={assessmentForm} layout="vertical" initialValues={{ kind: "CONTROLE_CONTINU", max_score: 20, coefficient: 1 }}
          // Changer la matière pré-remplit les surveillants avec son ou ses
          // enseignants ; on peut ensuite en ajouter ou en retirer. setFieldsValue
          // ne relance pas onValuesChange — pas de boucle.
          onValuesChange={(changed) => {
            if ("subject_id" in changed) {
              assessmentForm.setFieldsValue({ supervisor_ids: teachersOf(changed.subject_id) });
            }
          }}
          onFinish={(v) => api.createAssessment({ ...v, class_id: classId, term_id: termId,
            assessment_date: v.assessment_date ? dayjs(v.assessment_date).format("YYYY-MM-DDT00:00:00[Z]") : undefined })
            .then(() => { setOpen(false); load(); }).catch(apiError)}>
          <Form.Item name="title" label="Titre" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="subject_id" label="Matière" rules={[{ required: true }]}>
            <Select options={subjects.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="kind" label="Type">
            <Select options={[{ value: "CONTROLE_CONTINU", label: "Contrôle continu" }, { value: "EXAMEN", label: "Examen" }]} />
          </Form.Item>
          <Space>
            <Form.Item name="max_score" label="Barème"><InputNumber min={1} /></Form.Item>
            <Form.Item name="coefficient" label="Coefficient"><InputNumber min={0.5} step={0.5} /></Form.Item>
            <Form.Item name="assessment_date" label="Date"><DatePicker format="DD/MM/YYYY" /></Form.Item>
          </Space>
          <Form.Item name="room_id" label="Salle d'examen">
            <Select allowClear options={rooms.map((r) => ({ value: r.id, label: r.name }))} />
          </Form.Item>
          <Form.Item name="supervisor_ids" label="Surveillants"
            extra="Pré-rempli avec l'enseignant de la matière (réglé dans Personnel) ; ajoutez-en d'autres au besoin.">
            <Select mode="multiple" allowClear options={staff.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
          </Form.Item>
          <Button type="primary" htmlType="submit">Créer</Button>
        </Form>
      </Modal>

      <Modal title={scoring ? `Notes — ${scoring.title} (/${scoring.max_score})` : ""} open={!!scoring}
        // Fermer une saisie de notes en cours perdrait toute la classe : on
        // demande confirmation, et la modale reste ouverte si l'écriture échoue.
        onCancel={() => { confirmDiscard(scoresDirty).then((ok) => { if (ok) setScoring(null); }); }}
        destroyOnClose width={720}
        onOk={saveScores} okText="Enregistrer">
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          La remarque reste attachée à cette évaluation. L'appréciation qui figure sur le bulletin
          se saisit depuis le bulletin de l'élève.
        </Typography.Text>
        <Table<Score> rowKey="student_id" dataSource={scores} pagination={false} size="small"
          columns={[
            { title: "Élève", dataIndex: "student_name" },
            { title: `Note /${scoring?.max_score}`, width: 130, render: (_, s, i) => (
              <InputNumber
                min={0} max={scoring?.max_score} value={s.score ?? undefined}
                // Un élève marqué absent n'a pas de note : le champ se ferme
                // plutôt que d'accepter une saisie qui serait ignorée.
                disabled={s.is_absent}
                autoFocus={i === 0}
                ref={(el) => { scoreInputs.current[i] = el; }}
                // Entrée passe à l'élève suivant : une classe se saisit au
                // clavier, sans repasser par la souris à chaque ligne.
                onPressEnter={() => focusScore(i + 1)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") { e.preventDefault(); focusScore(i + 1); }
                  if (e.key === "ArrowUp") { e.preventDefault(); focusScore(i - 1); }
                }}
                onChange={(v) => { setScoresDirty(true); setScores((prev) => prev.map((p, j) => (j === i ? { ...p, score: v ?? null } : p))); }} />) },
            { title: "Absent", width: 80, align: "center", render: (_, s, i) => (
              // is_absent distingue « absent à l'épreuve » de « pas encore
              // noté » — les deux laissent la note vide, et sans ce marqueur
              // l'information était perdue. Cocher efface la note saisie.
              <Checkbox
                checked={!!s.is_absent}
                onChange={(e) => { setScoresDirty(true); setScores((prev) => prev.map((p, j) =>
                  (j === i ? { ...p, is_absent: e.target.checked, score: e.target.checked ? null : p.score } : p))); }} />) },
            // « Remarque » et non « appréciation » : elle porte sur CETTE note et
            // reste attachée à cette évaluation. L'appréciation du bulletin, qui
            // porte sur le trimestre entier et s'imprime, se saisit depuis le
            // bulletin — une matière compte plusieurs évaluations, leurs
            // remarques ne peuvent pas en tenir lieu.
            { title: "Remarque sur la note", render: (_, s, i) => (
              <Input
                placeholder="Facultatif — ex. « copie non rendue »" value={s.comment ?? ""}
                onChange={(e) => { setScoresDirty(true); setScores((prev) => prev.map((p, j) =>
                  (j === i ? { ...p, comment: e.target.value || undefined } : p))); }} />) },
          ]}
          // Récapitulatif de classe : la première question après une saisie est
          // « comment s'en est sortie la classe ? ». Calculé sur les notes
          // présentes à l'écran, sans aller-retour serveur.
          summary={() => {
            const graded = scores.filter((s) => s.score != null).map((s) => s.score as number);
            const absents = scores.filter((s) => s.is_absent).length;
            if (graded.length === 0) {
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Typography.Text type="secondary">
                      Aucune note saisie{absents > 0 ? ` — ${absents} absent(s)` : ""}
                    </Typography.Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }
            const avg = graded.reduce((a, b) => a + b, 0) / graded.length;
            const half = (scoring?.max_score ?? 20) / 2;
            const below = graded.filter((v) => v < half).length;
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Space wrap size={12} style={{ fontFamily: FONT_MONO, fontSize: 13 }}>
                    <span>Moyenne <strong>{avg.toFixed(2)}</strong></span>
                    <span>Min {Math.min(...graded)}</span>
                    <span>Max {Math.max(...graded)}</span>
                    <span>{graded.length} noté(s)</span>
                    {absents > 0 && <span>{absents} absent(s)</span>}
                    <span>{below} sous {half}</span>
                  </Space>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Modal>

      <Modal title={bulletin ? `Bulletin — ${bulletin.student_name} (${bulletin.term_label})` : ""} open={!!bulletin}
        onCancel={() => { confirmDiscard(apprDirty).then((ok) => { if (ok) setBulletin(null); }); }}
        footer={null} destroyOnClose width={860}>
        {bulletin && (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Classe">{bulletin.class_label || "—"} {bulletin.level_name ? `(${bulletin.level_name})` : ""}</Descriptions.Item>
              <Descriptions.Item label="Moyenne générale">
                <strong>{bulletin.overall_average != null ? bulletin.overall_average.toFixed(2) + " / 20" : "—"}</strong>
              </Descriptions.Item>
              {/* Rang absent tant que l'élève n'a aucune moyenne, ou s'il n'est
                  affecté à aucune classe : la ligne disparaît alors. */}
              {bulletin.rank != null && bulletin.class_size != null && (
                <Descriptions.Item label="Rang">
                  <strong>{bulletin.rank}</strong> / {bulletin.class_size}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Table rowKey="subject_id" dataSource={bulletin.subjects} pagination={false} size="small"
              columns={[
                { title: "Matière", dataIndex: "subject_name" },
                { title: "Coef", dataIndex: "coefficient", width: 70 },
                { title: "Moyenne /20", dataIndex: "average", width: 110, render: (v?: number) => (v != null ? v.toFixed(2) : "—") },
                { title: "Éval.", dataIndex: "assessment_count", width: 70 },
                {
                  title: "Appréciation du trimestre",
                  render: (_, s) => (
                    <Input
                      placeholder="Figure sur le bulletin imprimé"
                      value={apprSubjects[s.subject_id] ?? ""}
                      onChange={(e) => {
                        setApprDirty(true);
                        setApprSubjects((prev) => ({ ...prev, [s.subject_id]: e.target.value }));
                      }}
                    />
                  ),
                },
              ]} />

            {/* Appréciation générale : celle du conseil de classe, qui porte sur
                le trimestre et non sur une matière. */}
            <Typography.Text strong style={{ display: "block", marginTop: 16, marginBottom: 6 }}>
              Appréciation générale
            </Typography.Text>
            <Input.TextArea
              rows={3}
              value={apprGeneral}
              onChange={(e) => { setApprDirty(true); setApprGeneral(e.target.value); }}
              placeholder="Appréciation du conseil de classe — laissez vide pour ne rien imprimer."
            />

            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Space>
                <Button type="primary" loading={apprSaving} onClick={saveAppreciations}>
                  Enregistrer les appréciations
                </Button>
                <Button icon={<PrinterOutlined />} onClick={() => printBulletinPdf([bulletin])}>
                  Imprimer le PDF
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>
    </Space>
  );
};

// ── Assiduité ────────────────────────────────────────────────────────────────
// DAY_SLOT : valeur du sélecteur pour « journée entière ». 0 plutôt que
// undefined, qu'un Select antd confondrait avec « rien de choisi ».
const DAY_SLOT = 0;

// Durée proposée par défaut pour un retard, en minutes. Proposée, pas
// imposée : le champ se vide et se ressaisit.
const DEFAULT_LATE_MINUTES = 10;

// dayjs n'a pas la locale française chargée ici : on nomme les mois à la main
// plutôt que d'afficher « July » dans une interface française.
const MONTHS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
const monthLabelFr = (m: dayjs.Dayjs) => `${MONTHS_FR[m.month()]} ${m.year()}`;

// DayMark : marque agrégée d'un élève pour une journée. L'absence prime sur le
// retard ; « justifié » seulement si tout ce qui compose la journée l'est.
type DayMark = { kind: "ABSENCE" | "RETARD"; justified: boolean };

/**
 * printAttendanceRegister — registre d'assiduité mensuel imprimable (feuille
 * d'appel), même mécanisme d'impression que les autres documents. Grille
 * élèves × jours, A = absence, R = retard, souligné = justifié.
 */
const printAttendanceRegister = (
  win: Window,
  opts: {
    className: string;
    monthLabel: string;
    students: Student[];
    index: Map<number, Map<number, DayMark>>;
    days: number[];
    schoolName?: string;
    logoUrl?: string;
  }
): void => {
  const head = opts.days.map((d) => `<th>${d}</th>`).join("");
  const body = opts.students
    .map((s) => {
      const m = opts.index.get(s.id);
      let abs = 0, ret = 0;
      const cells = opts.days
        .map((d) => {
          const mark = m?.get(d);
          if (!mark) return `<td></td>`;
          const cls = mark.justified ? " just" : "";
          if (mark.kind === "ABSENCE") { abs++; return `<td class="abs${cls}">A</td>`; }
          ret++;
          return `<td class="ret${cls}">R</td>`;
        })
        .join("");
      return `<tr><th class="name">${escapeHtml(`${s.last_name} ${s.first_name}`)}</th>${cells}<td class="tot">${abs || ""}</td><td class="tot">${ret || ""}</td></tr>`;
    })
    .join("");

  win.document.open();
  win.document.write(`<!doctype html>
<html lang="fr"><head><meta charset="utf-8" />
<title>Registre — ${escapeHtml(opts.className)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1f1f1f; margin: 0; }
  .head { text-align: center; margin-bottom: 10px; }
  .head img { max-height: 50px; max-width: 160px; object-fit: contain; margin-bottom: 4px; }
  .head h2 { margin: 0; font-size: 15px; }
  .head h1 { margin: 3px 0 0; font-size: 16px; text-decoration: underline; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #999; padding: 2px 3px; font-size: 9.5px; text-align: center; }
  thead th { background: #f0f0f0; }
  th.name { width: 130px; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  td.abs { color: #c62828; font-weight: 700; }
  td.ret { color: #d48806; font-weight: 700; }
  td.just { text-decoration: underline; }
  td.tot { font-weight: 700; background: #fafafa; }
  .legend { margin-top: 10px; font-size: 11px; color: #555; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="head">
    ${opts.logoUrl ? `<img src="${escapeHtml(opts.logoUrl)}" alt="" />` : ""}
    <h2>${escapeHtml(opts.schoolName || "Établissement scolaire")}</h2>
    <h1>Registre d'assiduité — ${escapeHtml(opts.className)} — ${escapeHtml(opts.monthLabel)}</h1>
  </div>
  <table>
    <thead><tr><th class="name">Élève</th>${head}<th>Abs</th><th>Ret</th></tr></thead>
    <tbody>${body}</tbody>
  </table>
  <div class="legend">A = absence · R = retard · souligné = justifié</div>
  <script>
    (function () {
      var img = document.querySelector('img');
      function go() { window.focus(); window.print(); }
      if (!img) { go(); return; }
      if (img.complete) { go(); return; }
      img.addEventListener('load', go);
      img.addEventListener('error', function () { img.style.display = 'none'; go(); });
      setTimeout(go, 1500);
    })();
  </script>
</body></html>`);
  win.document.close();
};

const AttendanceTab: React.FC<{ classId?: number; yearId?: number; classLabel?: string }> = ({ classId, yearId, classLabel }) => {
  const api = usePedagogyApi();
  const school = useSchoolApi();
  const profileApi = useProfileApi();
  const [view, setView] = useState<"day" | "month">("day");
  const [date, setDate] = useState(dayjs());
  const [month, setMonth] = useState(dayjs());
  const [students, setStudents] = useState<Student[]>([]);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [monthRecords, setMonthRecords] = useState<AttendanceRecord[]>([]);
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [slotId, setSlotId] = useState<number>(DAY_SLOT);
  const [profile, setProfile] = useState<SchoolProfile | null>(null);
  // Durée du retard en cours de saisie. Une seule valeur suffit : un seul
  // formulaire de retard est ouvert à la fois.
  const [lateMinutes, setLateMinutes] = useState<number | null>(DEFAULT_LATE_MINUTES);

  const load = useCallback(() => {
    if (classId) api.listAttendanceByClass(classId, date.format("YYYY-MM-DD")).then(setRecords);
  }, [api, classId, date]);
  useEffect(load, [load]);
  useEffect(() => {
    if (classId && yearId) {
      school.listStudents({ class_id: classId, school_year_id: yearId, page_size: 200 })
        .then((r) => setStudents(r.items ?? []));
    }
  }, [school, classId, yearId]);
  useEffect(() => {
    if (classId) api.listSlots(classId).then(setSlots).catch(() => setSlots([]));
  }, [api, classId]);
  useEffect(() => { profileApi.get().then(setProfile).catch(() => undefined); }, [profileApi]);

  // Pointages du mois affiché — vue mensuelle et registre.
  useEffect(() => {
    if (view === "month" && classId) {
      api.listAttendanceByClassRange(
        classId, month.startOf("month").format("YYYY-MM-DD"), month.endOf("month").format("YYYY-MM-DD"),
      ).then(setMonthRecords).catch(() => setMonthRecords([]));
    }
  }, [api, view, classId, month]);

  // Grille du mois : jours en colonnes, index (élève → jour → marque agrégée).
  const monthDays = useMemo(
    () => Array.from({ length: month.daysInMonth() }, (_, i) => i + 1),
    [month]
  );
  const monthIndex = useMemo(() => {
    const map = new Map<number, Map<number, DayMark>>();
    monthRecords.forEach((r) => {
      const d = dayjs(r.event_date);
      if (d.month() !== month.month() || d.year() !== month.year()) return;
      const day = d.date();
      let m = map.get(r.student_id);
      if (!m) { m = new Map(); map.set(r.student_id, m); }
      const prev = m.get(day);
      const kind: "ABSENCE" | "RETARD" = prev?.kind === "ABSENCE" || r.kind === "ABSENCE" ? "ABSENCE" : "RETARD";
      m.set(day, { kind, justified: (prev?.justified ?? true) && r.justified });
    });
    return map;
  }, [monthRecords, month]);
  const totalsOf = (id: number) => {
    let abs = 0, ret = 0;
    monthIndex.get(id)?.forEach((v) => (v.kind === "ABSENCE" ? abs++ : ret++));
    return { abs, ret };
  };

  const printRegister = () => {
    if (!classId || students.length === 0) return;
    const win = window.open("", "_blank", "width=1100,height=800");
    if (!win) { message.error("Autorisez les fenêtres pop-up pour imprimer le registre"); return; }
    printAttendanceRegister(win, {
      className: classLabel ?? "Classe",
      monthLabel: monthLabelFr(month),
      students, index: monthIndex, days: monthDays,
      schoolName: profile?.school_name, logoUrl: profile?.logo_url,
    });
  };

  // Séances du jour affiché. dayjs().day() rend 0 le dimanche là où
  // day_of_week suit la norme ISO (1 = lundi … 7 = dimanche).
  const isoDow = date.day() === 0 ? 7 : date.day();
  const daySlots = slots.filter((s) => s.day_of_week === isoDow);

  // Changer de date peut faire disparaître la séance choisie (elle n'a pas lieu
  // ce jour-là) : on retombe alors sur la journée entière, sinon on saisirait
  // sur une séance invisible — que le backend refuserait de toute façon.
  useEffect(() => {
    if (slotId !== DAY_SLOT && !daySlots.some((s) => s.id === slotId)) setSlotId(DAY_SLOT);
  }, [slotId, daySlots]);

  const slotLabel = (s: TimetableSlot) => `${s.start_time}–${s.end_time} — ${s.subject_name ?? "?"}`;
  const recordLabel = (r: AttendanceRecord) =>
    r.slot_start ? `${r.slot_start} ${r.slot_subject ?? ""}`.trim() : "Journée";

  // Type de retour explicite : `retry` rappelle mark, ce qui rend la fonction
  // auto-référente — TypeScript ne peut alors plus l'inférer.
  const mark = (studentId: number, kind: "ABSENCE" | "RETARD", minutes?: number): Promise<void> =>
    api.recordAttendance(studentId, {
      kind, class_id: classId,
      slot_id: slotId === DAY_SLOT ? undefined : slotId,
      event_date: date.format("YYYY-MM-DD") + "T00:00:00Z",
      minutes_late: kind === "RETARD" ? minutes : undefined,
    }).then((rec) => {
      message.success(WHATSAPP_ENABLED
        ? (rec.notified_at ? "Enregistré — parent notifié via WhatsApp" : "Enregistré (aucun tuteur à notifier)")
        : "Enregistré");
      load();
    }).catch((err) => notifyWriteError(err, {
      title: "Pointage non enregistré",
      retry: () => mark(studentId, kind, minutes),
    }));

  return (
    <Card
      extra={
        <Space wrap>
          {/* Deux lectures de la même assiduité : saisie du jour, ou vue
              mensuelle en grille (registre imprimable). */}
          <Segmented
            value={view}
            onChange={(v) => setView(v as "day" | "month")}
            options={[{ value: "day", label: "Jour" }, { value: "month", label: "Mois" }]}
          />
          {view === "day" ? (
            <>
              <Select
                value={slotId}
                onChange={setSlotId}
                style={{ minWidth: 240 }}
                options={[
                  { value: DAY_SLOT, label: "Journée entière" },
                  ...daySlots.map((s) => ({ value: s.id, label: slotLabel(s) })),
                ]}
              />
              <DatePicker value={date} onChange={(d) => d && setDate(d)} format="DD/MM/YYYY" allowClear={false} />
            </>
          ) : (
            <>
              <DatePicker
                picker="month"
                value={month}
                onChange={(d) => d && setMonth(d)}
                format={(v) => monthLabelFr(v)}
                allowClear={false}
              />
              <Button
                icon={<PrinterOutlined />}
                disabled={!classId || students.length === 0}
                onClick={printRegister}
              >
                Imprimer le registre
              </Button>
            </>
          )}
        </Space>
      }
    >
      {!classId && <Typography.Text type="secondary">Sélectionnez une classe.</Typography.Text>}

      {classId && view === "day" && (
        <>
          {daySlots.length === 0 && (
            <Typography.Text type="secondary">
              Aucune séance à l'emploi du temps ce jour-là — saisie à la journée uniquement.
            </Typography.Text>
          )}
          <Table<Student> rowKey="id" dataSource={students} pagination={false} size="small" sticky
            columns={[
              { title: "Élève", render: (_, s) => `${s.last_name} ${s.first_name}` },
              { title: "Journée", render: (_, s) => {
                const day = records.filter((r) => r.student_id === s.id);
                if (day.length === 0) return <Tag>Présent</Tag>;
                return (
                  <Space wrap>
                    {day.map((rec) => (
                      <Tag key={rec.id} color={rec.kind === "ABSENCE" ? "red" : "orange"}>
                        {rec.kind} · {recordLabel(rec)}
                        {rec.justified ? " · justifié" : ""}
                      </Tag>
                    ))}
                  </Space>
                );
              } },
              { title: "Séance sélectionnée", render: (_, s) => {
                // Une seule saisie par (élève, date, séance) : la re-saisie remplace.
                const rec = records.find(
                  (r) => r.student_id === s.id && (r.slot_id ?? DAY_SLOT) === slotId
                );
                if (!rec) return <Tag>Présent</Tag>;
                return (
                  <Space>
                    <Tag color={rec.kind === "ABSENCE" ? "red" : "orange"}>{rec.kind}</Tag>
                    {WHATSAPP_ENABLED && rec.notified_at && <Tag icon={<WhatsAppOutlined />} color="green">Notifié</Tag>}
                    {rec.justified && <Tag color="blue">Justifié</Tag>}
                    {!rec.justified && (
                      <Button size="small" onClick={() => api.justifyAttendance(rec.id, adminJustification(rec.kind)).then(load)}>
                        Justifier
                      </Button>
                    )}
                  </Space>
                );
              } },
              { title: "Actions", render: (_, s) => (
                <Space>
                  <Button size="small" danger onClick={() => mark(s.id, "ABSENCE")}>Absence</Button>
                  {/* La durée est demandée au lieu d'être supposée : elle était
                      figée à 10 minutes pour tout le monde, ce qui rendait la
                      colonne « minutes » inexploitable. */}
                  <Popconfirm
                    title="Retard de combien de minutes ?"
                    description={
                      <InputNumber
                        min={1} max={240} style={{ width: 120 }} addonAfter="min"
                        // `?? 10` réécrivait 10 dans le champ à l'instant même
                        // où on le vidait : impossible d'effacer pour saisir
                        // une autre durée. Le vide est donc conservé, et c'est
                        // la validation qui est bloquée tant qu'il dure.
                        value={lateMinutes} onChange={(v) => setLateMinutes(v)}
                      />
                    }
                    okText="Enregistrer"
                    cancelText="Annuler"
                    onOpenChange={(isOpen) => { if (isOpen) setLateMinutes(DEFAULT_LATE_MINUTES); }}
                    okButtonProps={{ disabled: lateMinutes == null }}
                    onConfirm={() => mark(s.id, "RETARD", lateMinutes ?? DEFAULT_LATE_MINUTES)}
                  >
                    <Button size="small">Retard</Button>
                  </Popconfirm>
                </Space>) },
            ]} />
        </>
      )}

      {classId && view === "month" && (
        <>
          {/* Grille mensuelle : A = absence (rouge, bleu si justifiée),
              R = retard. Colonnes de totaux à droite. */}
          <Table<Student>
            rowKey="id"
            dataSource={students}
            pagination={false}
            size="small"
            scroll={{ x: 160 + monthDays.length * 34 + 110 }}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun élève dans cette classe" />,
            }}
            columns={[
              {
                title: "Élève",
                fixed: "left",
                width: 160,
                render: (_, s) => <span style={{ whiteSpace: "nowrap" }}>{s.last_name} {s.first_name}</span>,
              },
              ...monthDays.map((d) => ({
                title: String(d),
                key: `d${d}`,
                width: 34,
                align: "center" as const,
                // Padding réduit : sinon un numéro à deux chiffres (« 10 », « 31 »)
                // n'entre pas dans la colonne et passe à la ligne.
                onHeaderCell: () => ({ style: { padding: "8px 2px" } }),
                onCell: () => ({ style: { padding: "4px 2px" } }),
                render: (_: unknown, s: Student) => {
                  const mk = monthIndex.get(s.id)?.get(d);
                  if (!mk) return null;
                  const color = mk.kind === "ABSENCE" ? (mk.justified ? "#1677ff" : "#ff4d4f") : "#d48806";
                  return (
                    <span style={{ color, fontWeight: 600, textDecoration: mk.justified ? "underline" : undefined }}>
                      {mk.kind === "ABSENCE" ? "A" : "R"}
                    </span>
                  );
                },
              })),
              { title: "Abs", width: 52, align: "center" as const, fixed: "right" as const, render: (_: unknown, s: Student) => totalsOf(s.id).abs || null },
              { title: "Ret", width: 52, align: "center" as const, fixed: "right" as const, render: (_: unknown, s: Student) => totalsOf(s.id).ret || null },
            ]}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            A = absence · R = retard · souligné = justifié
          </Typography.Text>
        </>
      )}
    </Card>
  );
};

// ── Cahier de texte & devoirs ────────────────────────────────────────────────
const TextbookTab: React.FC<{ classId?: number }> = ({ classId }) => {
  const api = usePedagogyApi();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [subjectId, setSubjectId] = useState<number>();
  const [entries, setEntries] = useState<any[]>([]);
  const [homework, setHomework] = useState<any[]>([]);
  const [program, setProgram] = useState<any[]>([]);
  const [entryForm] = Form.useForm();
  const [homeworkForm] = Form.useForm();

  useEffect(() => { api.listSubjects().then(setSubjects); }, [api]);
  const load = useCallback(() => {
    if (!classId) return;
    api.listTextbook(classId, subjectId).then(setEntries);
    api.listHomework(classId).then(setHomework);
    if (subjectId) api.programProgress(classId, subjectId).then(setProgram).catch(() => setProgram([]));
  }, [api, classId, subjectId]);
  useEffect(load, [load]);

  // La matière choisie en haut se propage aux deux formulaires : on la
  // sélectionne une fois, « Ajouter » et « Ajouter un devoir » la reprennent
  // (toujours modifiable ponctuellement dans chaque formulaire). Vidée en haut,
  // elle se vide aussi dans les formulaires.
  useEffect(() => {
    entryForm.setFieldsValue({ subject_id: subjectId });
    homeworkForm.setFieldsValue({ subject_id: subjectId });
  }, [subjectId, entryForm, homeworkForm]);

  // Devoirs filtrés par la matière du haut (le cahier de texte l'est déjà côté
  // serveur) : l'onglet se lit alors comme l'historique d'une seule matière.
  const shownHomework = subjectId ? homework.filter((h) => h.subject_id === subjectId) : homework;

  return (
    <Card extra={<Select placeholder="Matière" allowClear style={{ width: 180 }} value={subjectId} onChange={setSubjectId}
      options={subjects.map((s) => ({ value: s.id, label: s.name }))} />}>
      {subjectId && program.length > 0 && (
        <>
          <Typography.Title level={5}>Suivi de programme</Typography.Title>
          <Space wrap style={{ marginBottom: 16 }}>
            {program.map((p) => <Tag key={p.id} color={p.covered ? "green" : "default"}>{p.title}</Tag>)}
          </Space>
        </>
      )}
      <Typography.Title level={5}>Cahier de texte</Typography.Title>
      <Form form={entryForm} layout="inline" style={{ marginBottom: 12 }}
        onFinish={(v) => api.createTextbook({ ...v, class_id: classId, subject_id: v.subject_id ?? subjectId,
          entry_date: dayjs().format("YYYY-MM-DD") + "T00:00:00Z" })
          .then(() => { entryForm.resetFields(); entryForm.setFieldsValue({ subject_id: subjectId }); load(); }).catch(apiError)}>
        <Form.Item name="subject_id">
          <Select placeholder="Matière" style={{ width: 150 }} options={subjects.map((s) => ({ value: s.id, label: s.name }))} />
        </Form.Item>
        <Form.Item name="content" rules={[{ required: true, message: "Contenu requis" }]} style={{ flex: 1, minWidth: 260 }}>
          <Input placeholder="Contenu de la séance…" />
        </Form.Item>
        <Button htmlType="submit" type="primary" disabled={!classId}>Ajouter</Button>
      </Form>
      <List dataSource={entries} locale={{ emptyText: "Aucune entrée" }}
        renderItem={(e: any) => (
          // Suppression courante et sans enjeu : on agit tout de suite et on
          // laisse 6 s pour annuler (modèle Gmail), plutôt que d'imposer une
          // confirmation à chaque ligne. Rien n'est envoyé au serveur avant la
          // fin du délai : annuler n'a donc rien à défaire.
          <List.Item actions={[
            <Button
              key="d" type="text" danger size="small"
              onClick={() => runUndoable({
                label: "Entrée supprimée",
                onOptimistic: () => setEntries((prev) => prev.filter((x: any) => x.id !== e.id)),
                onRevert: load,
                run: () => api.deleteTextbook(e.id),
                onDone: load,
              })}
            >
              Supprimer
            </Button>]}>
            <List.Item.Meta title={`${dayjs(e.entry_date).format("DD/MM/YYYY")} — ${e.subject_name}`} description={e.content} />
          </List.Item>
        )} />
      <Typography.Title level={5}>Devoirs</Typography.Title>
      <List dataSource={shownHomework} locale={{ emptyText: "Aucun devoir" }}
        renderItem={(h: any) => (
          <List.Item actions={[
            <Button
              key="d" type="text" danger size="small"
              onClick={() => runUndoable({
                label: "Devoir supprimé",
                onOptimistic: () => setHomework((prev) => prev.filter((x: any) => x.id !== h.id)),
                onRevert: load,
                run: () => api.deleteHomework(h.id),
                onDone: load,
              })}
            >
              Supprimer
            </Button>]}>
            <List.Item.Meta title={`${h.title} — pour le ${dayjs(h.due_date).format("DD/MM/YYYY")}`}
              description={`${h.subject_name}${h.description ? " · " + h.description : ""}`} />
          </List.Item>
        )} />
      <Form form={homeworkForm} layout="inline"
        onFinish={(v) => api.createHomework({ ...v, class_id: classId, subject_id: v.subject_id ?? subjectId,
          due_date: dayjs(v.due_date).format("YYYY-MM-DD") + "T00:00:00Z" })
          .then(() => { homeworkForm.resetFields(); homeworkForm.setFieldsValue({ subject_id: subjectId }); load(); }).catch(apiError)}>
        <Form.Item name="subject_id" rules={[{ required: true, message: "Matière requise" }]}>
          <Select placeholder="Matière" style={{ width: 150 }} options={subjects.map((s) => ({ value: s.id, label: s.name }))} />
        </Form.Item>
        <Form.Item name="title" rules={[{ required: true, message: "Intitulé requis" }]}><Input placeholder="Devoir…" /></Form.Item>
        <Form.Item name="due_date" rules={[{ required: true, message: "Échéance requise" }]}><DatePicker format="DD/MM/YYYY" /></Form.Item>
        <Button htmlType="submit" disabled={!classId}>Ajouter un devoir</Button>
      </Form>
    </Card>
  );
};

// ── Admin pédagogique (matières, enseignants, salles, trimestres, coefficients) ──
export default PedagogyPage;
